/**
 * The Day plan: clock times are labels the app puts on positions. SharePoint keeps day, period and
 * position (1st, 2nd, 3rd visit); the admin's Day plan settings (SM_Settings rows) turn position 1, 2, 3…
 * into 9:30, 10:30, 1:00… Changing a time touches no rule.
 *
 *   DAY_PLAN_START            first visit of the day            (default 9:30)
 *   DAY_PLAN_VISIT_MINUTES    each position gets this long      (default 60)
 *   DAY_PLAN_LUNCH_START      no visit starts inside lunch or runs into it (default 12:00)
 *   DAY_PLAN_LUNCH_MINUTES    (default 60)
 *   DAY_PLAN_END              last visit ends by                (default 16:00)
 *   DAY_PLAN_OVERRIDE_<yyyyMMdd>  a one-day start, like an early start (tomorrow goes back to the plan)
 *
 * No dependencies, so the app and the API share it.
 */

export interface DayPlan {
  startMinutes: number;
  visitMinutes: number;
  lunchStartMinutes: number;
  lunchMinutes: number;
  endMinutes: number;
}

export const DAY_PLAN_KEYS = ['DAY_PLAN_START', 'DAY_PLAN_VISIT_MINUTES', 'DAY_PLAN_LUNCH_START', 'DAY_PLAN_LUNCH_MINUTES', 'DAY_PLAN_END'] as const;
export const DAY_PLAN_OVERRIDE_PREFIX = 'DAY_PLAN_OVERRIDE_';

/** The reference's default plan: 9:30 · 10:30 · lunch 12–1 · 1:00 · 2:00 · 3:00. */
export const DEFAULT_DAY_PLAN: DayPlan = {
  startMinutes: 9 * 60 + 30,
  visitMinutes: 60,
  lunchStartMinutes: 12 * 60,
  lunchMinutes: 60,
  endMinutes: 16 * 60,
};

/** The default settings rows, as SM_Settings stores them (SettingValue is text). */
export const DEFAULT_DAY_PLAN_SETTINGS: Record<(typeof DAY_PLAN_KEYS)[number], string> = {
  DAY_PLAN_START: '09:30',
  DAY_PLAN_VISIT_MINUTES: '60',
  DAY_PLAN_LUNCH_START: '12:00',
  DAY_PLAN_LUNCH_MINUTES: '60',
  DAY_PLAN_END: '16:00',
};

export type DayPlanSettings = Record<string, string | number | null | undefined>;

/** "9:30", "09:30", "13:00" or a number of minutes after midnight → minutes; null when unreadable. */
export function parseClock(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) && v >= 0 && v < 24 * 60 ? Math.round(v) : null;
  const s = v.trim();
  const m = /^(\d{1,2}):(\d{2})\s*(am|pm)?$/i.exec(s);
  if (m) {
    let h = Number(m[1]);
    const min = Number(m[2]);
    const ap = m[3]?.toLowerCase();
    if (ap === 'pm' && h < 12) h += 12;
    if (ap === 'am' && h === 12) h = 0;
    if (h > 23 || min > 59) return null;
    return h * 60 + min;
  }
  if (/^\d+$/.test(s)) return parseClock(Number(s));
  return null;
}

function parseMinutes(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).trim());
  return Number.isFinite(n) && n > 0 && n <= 8 * 60 ? Math.round(n) : null;
}

/** The plan for one civil day: the settings, with that day's override start if there is one. */
export function dayPlanFor(dayKey: string, settings: DayPlanSettings = {}): DayPlan {
  const d = DEFAULT_DAY_PLAN;
  const override = parseClock(settings[`${DAY_PLAN_OVERRIDE_PREFIX}${dayKey}`]);
  return {
    startMinutes: override ?? parseClock(settings.DAY_PLAN_START) ?? d.startMinutes,
    visitMinutes: parseMinutes(settings.DAY_PLAN_VISIT_MINUTES) ?? d.visitMinutes,
    lunchStartMinutes: parseClock(settings.DAY_PLAN_LUNCH_START) ?? d.lunchStartMinutes,
    lunchMinutes: parseMinutes(settings.DAY_PLAN_LUNCH_MINUTES) ?? d.lunchMinutes,
    endMinutes: parseClock(settings.DAY_PLAN_END) ?? d.endMinutes,
  };
}

export interface PositionTime {
  position: number;
  startMinutes: number;
  endMinutes: number;
}

/**
 * Position times for a day: the first at the start time, each the visit length apart, none starting
 * inside lunch or running into it (a visit that would is moved to lunch's end), none running past the
 * end. `atLeast` extends the list past the end of the day (a surge position still gets a label).
 */
export function positionTimes(dayKey: string, settings: DayPlanSettings = {}, atLeast = 0): PositionTime[] {
  const p = dayPlanFor(dayKey, settings);
  const lunchEnd = p.lunchStartMinutes + p.lunchMinutes;
  const out: PositionTime[] = [];
  let t = p.startMinutes;
  for (let guard = 0; guard < 48; guard++) {
    if (p.lunchMinutes > 0 && t < lunchEnd && t + p.visitMinutes > p.lunchStartMinutes) t = Math.max(t, lunchEnd);
    const end = t + p.visitMinutes;
    if (end > p.endMinutes && out.length >= atLeast) break;
    if (end > 24 * 60) break;
    out.push({ position: out.length + 1, startMinutes: t, endMinutes: end });
    t = end;
  }
  return out;
}

/** The start time label for one position, or null when the plan has no room for it. */
export function positionStart(dayKey: string, position: number, settings: DayPlanSettings = {}): number | null {
  return positionTimes(dayKey, settings, position).find((x) => x.position === position)?.startMinutes ?? null;
}

/** 570 → "9:30" (12-hour, no AM/PM — the reference's style); 780 → "1:00". */
export function formatClock(minutes: number, style: '12h' | '24h' = '12h'): string {
  const h24 = Math.floor(minutes / 60);
  const m = String(minutes % 60).padStart(2, '0');
  if (style === '24h') return `${String(h24).padStart(2, '0')}:${m}`;
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h}:${m}`;
}

/* ------------------------------------------------------------------------------------------------
 * 1.5.0 — each visit's length, the end of the crew's day, and the running day.
 *
 * A visit's length comes from the booking (the Book sheet's "How long", kept in the request's form), else
 * HK_DEEP_LENGTH for a deep clean, else DAY_PLAN_VISIT_MINUTES. The day is laid out position by position from
 * the start time, each visit its own length (a free position the plan's length). The lunch rule stands for a
 * visit no longer than the plan's visit; a longer one (an extended visit, a deep clean) may run through lunch,
 * and its span includes the break. DAY_PLAN_END is the crew's end of day: a visit that would end after it is
 * never offered as a normal slot (docs/DECISIONS.md D-1.5.0-6).
 *
 * Today, once the crew has started (an Arrived or a Finish stamp), each later visit gets an expected time: the
 * visit in progress ends at Arrived + its length, a finished one at its Finish stamp, and the next starts the
 * gap (HK_GAP_MINUTES, default 10) after that. A free position takes no time on the running day; a free time the
 * crew has gone past is "passed". Tenants are never told of drift by the app: the office calls.
 * ---------------------------------------------------------------------------------------------- */

const steps = (from: number, to: number) => {
  const out: number[] = [];
  for (let m = from; m <= to; m += 30) out.push(m);
  return out;
};
/** Length of a visit (DAY_PLAN_VISIT_MINUTES and the Book sheet's How long): 30 min to 4 h 30. */
export const VISIT_LENGTH_STEPS: readonly number[] = steps(30, 270);
/** Length of a deep clean (HK_DEEP_LENGTH): 2 h to 4 h 30. */
export const DEEP_LENGTH_STEPS: readonly number[] = steps(120, 270);
export const GAP_STEPS: readonly number[] = [5, 10, 15];
export const DEFAULT_GAP_MINUTES = 10;
export const DEFAULT_DEEP_MINUTES = 180;
/** A staff unit's visit when nobody chose a length (HK_STAFF_LENGTH; the August rule: real ~90-minute work). */
export const DEFAULT_STAFF_MINUTES = 90;
/** An office's visit when nobody chose a length (HK_OFFICE_LENGTH, 1.7.8: "offices are 30 minutes, sometimes an hour"). */
export const DEFAULT_OFFICE_MINUTES = 30;

/** "30 min", "1 h", "1 h 30", … "4 h 30" — the same in English and Spanish. */
export function lengthWords(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${String(m).padStart(2, '0')}` : `${h} h`;
}

/** The gap between visits on the running day (HK_GAP_MINUTES: 5, 10 or 15). */
export function gapMinutes(settings: DayPlanSettings = {}): number {
  const n = Number(settings.HK_GAP_MINUTES);
  return GAP_STEPS.includes(n) ? n : DEFAULT_GAP_MINUTES;
}

/** A deep clean's length (HK_DEEP_LENGTH, minutes). */
export function deepMinutes(settings: DayPlanSettings = {}): number {
  const n = Number(settings.HK_DEEP_LENGTH);
  return Number.isFinite(n) && n >= 60 && n <= 8 * 60 ? n : DEFAULT_DEEP_MINUTES;
}

/** A staff unit's visit when nobody chose a length (HK_STAFF_LENGTH, minutes — 1.6.4). */
export function staffMinutes(settings: DayPlanSettings = {}): number {
  const n = Number(settings.HK_STAFF_LENGTH);
  return Number.isFinite(n) && n >= 30 && n <= 8 * 60 ? n : DEFAULT_STAFF_MINUTES;
}

/** An office's visit when nobody chose a length (HK_OFFICE_LENGTH, minutes — 1.7.8). */
export function officeMinutes(settings: DayPlanSettings = {}): number {
  const n = Number(settings.HK_OFFICE_LENGTH);
  return Number.isFinite(n) && n >= 30 && n <= 8 * 60 ? n : DEFAULT_OFFICE_MINUTES;
}

/** A length the app takes for one visit: 30 minutes to 8 hours (the Book sheet's How long, a direct booking's). */
export function validLength(v: unknown): number | null {
  const n = Number(v);
  return v !== null && v !== undefined && v !== '' && Number.isFinite(n) && n >= 30 && n <= 8 * 60 ? n : null;
}

/**
 * The length a booking starts with on the Book sheet: the setting for that kind of clean. A staff unit has its
 * own (1.6.4), an office its own (1.7.8 — an apartment the staff-units list marks "office"); a facility space takes
 * no position and keeps the plan's.
 */
export function defaultLength(settings: DayPlanSettings, deep: boolean, kind: 'tenant' | 'staff' | 'facility' | 'office' = 'tenant'): number {
  if (deep) return deepMinutes(settings);
  if (kind === 'office') return officeMinutes(settings);
  return kind === 'staff' ? staffMinutes(settings) : dayPlanFor('', settings).visitMinutes;
}

export interface PlannedVisit {
  position: number;
  minutes: number;
  /** Minutes after midnight on the service clock (today only). */
  arrivedAt?: number | null;
  finishedAt?: number | null;
  /**
   * 1.7.8 (D-1.7.8-0): a start the office chose for the day itself, minutes after midnight on the service clock. It
   * replaces the computed time of this one visit; nothing about its position changes and no other label moves.
   */
  startOverride?: number | null;
  /** 1.7.8: booked at a chosen time — its position keeps a free position's place on the plan, as before the booking. */
  overrideFree?: boolean;
}

export interface PlannedPosition {
  position: number;
  startMinutes: number;
  endMinutes: number;
  minutes: number;
  booked: boolean;
  /** The running day (today, once the crew has started): when it is now expected. */
  expectedStart: number | null;
  expectedEnd: number | null;
  /** A free time the crew can no longer reach today. */
  passed: boolean;
  /** 1.6.6, a two-crew day: the crew whose lane the position is in, and its place in that lane. */
  lane?: 'A' | 'B';
  laneIndex?: number;
  /** 1.7.8: the start is one the office chose (the visit is pinned to it; the running day does not move it). */
  chosen?: boolean;
}

/** 1.6.6: where a position sits on a two-crew day — the crew, and its place (1, 2, 3…) in that crew's lane. */
export interface LaneSpot {
  crew: 'A' | 'B';
  n: number;
}
export type LaneSpots = Record<number, LaneSpot>;

/** The place a position has on its lane's clock: itself on a one-crew day. */
export function lanePosition(lanes: LaneSpots | null | undefined, position: number): number {
  return lanes?.[position]?.n ?? position;
}

export interface LiveOptions {
  live?: boolean;
  nowMinutes?: number;
  gapMinutes?: number;
  /**
   * 1.6.6: the day has two crews — each lane is laid out on its own clock from the day's start (both can have a
   * 9:30; the lunch rule and the running day apply per lane). Absent: one crew, the day as it always was.
   */
  lanes?: LaneSpots | null;
}

/**
 * Eli, 13 Oct 2026: "They had nothing to do from 11:11 to 12." On the running day (today, once the crew is ahead of the
 * plan), a visit that would run into lunch by this much or less is done first and lunch starts when it ends; more than
 * this and it waits until after lunch, as before. The plan's own labels never change.
 */
export const LUNCH_FLEX_MINUTES = 30;

/** Lunch on one plan of the day: where it sits now (it can move later once, by the flex rule). */
interface LunchWindow {
  start: number;
  end: number;
  moved: boolean;
}
const lunchOf = (p: DayPlan): LunchWindow => ({ start: p.lunchStartMinutes, end: p.lunchStartMinutes + p.lunchMinutes, moved: false });

/**
 * Puts a visit around lunch: a standard visit never starts in lunch; one that would run into it by no more than
 * LUNCH_FLEX_MINUTES goes first and lunch follows it; otherwise it waits until after lunch. A longer one may run
 * through it.
 */
function placeAround(t: number, len: number, p: DayPlan, lunch: LunchWindow = { ...lunchOf(p), moved: true }): { start: number; end: number } {
  if (p.lunchMinutes <= 0) return { start: t, end: t + len };
  if (t >= lunch.start && t < lunch.end) return { start: lunch.end, end: lunch.end + len };
  if (t < lunch.start && t + len > lunch.start) {
    if (len <= p.visitMinutes) {
      if (!lunch.moved && len > 0 && t + len - lunch.start <= LUNCH_FLEX_MINUTES) {
        lunch.start = t + len;
        lunch.end = t + len + p.lunchMinutes;
        lunch.moved = true;
        return { start: t, end: t + len };
      }
      return { start: lunch.end, end: lunch.end + len };
    }
    return { start: t, end: t + len + p.lunchMinutes };
  }
  return { start: t, end: t + len };
}

/** A two-crew day: each lane planned as its own day, then told by the positions SharePoint holds. */
function planLanes(dayKey: string, settings: DayPlanSettings, visits: readonly PlannedVisit[], count: number, o: LiveOptions): PlannedPosition[] {
  const spots = o.lanes!;
  const one: LiveOptions = { ...o, lanes: null };
  const n = Math.max(count, ...visits.map((v) => v.position), 0);
  const out: PlannedPosition[] = [];
  for (const crew of ['A', 'B'] as const) {
    const byPlace = new Map<number, number>();
    for (let pos = 1; pos <= n; pos++) if (spots[pos]?.crew === crew) byPlace.set(spots[pos]!.n, pos);
    if (!byPlace.size) continue;
    const mine = visits.filter((v) => spots[v.position]?.crew === crew).map((v) => ({ ...v, position: spots[v.position]!.n }));
    for (const x of planDay(dayKey, settings, mine, Math.max(...byPlace.keys()), one)) {
      const pos = byPlace.get(x.position);
      // a place in the lane no position holds is empty time: nothing to show, nothing to book
      if (pos !== undefined) out.push({ ...x, position: pos, lane: crew, laneIndex: x.position });
    }
  }
  // a position the layout does not name (never the case with a complete layout) keeps the one-crew plan
  if (out.length < n) for (const x of planDay(dayKey, settings, visits, n, one)) if (!out.some((y) => y.position === x.position)) out.push(x);
  return out.sort((a, b) => a.position - b.position);
}

/** The day, position 1…count, with each visit's span (and, live, its expected time). */
export function planDay(dayKey: string, settings: DayPlanSettings, visits: readonly PlannedVisit[], count: number, o: LiveOptions = {}): PlannedPosition[] {
  if (o.lanes) return planLanes(dayKey, settings, visits, count, o);
  const p = dayPlanFor(dayKey, settings);
  const byPos = new Map(visits.map((v) => [v.position, v]));
  const n = Math.max(count, ...visits.map((v) => v.position), 0);
  const out: PlannedPosition[] = [];
  let t = p.startMinutes;
  // the plan's labels keep lunch where the Day plan puts it; only the running day moves it (below)
  const lunch: LunchWindow = { ...lunchOf(p), moved: true };
  for (let pos = 1; pos <= n; pos++) {
    const v = byPos.get(pos);
    const len = v?.minutes && v.minutes > 0 ? v.minutes : p.visitMinutes;
    const pin = v?.startOverride ?? null;
    // 1.7.8: a chosen start pins this one visit; its position still takes its place in the sequence, so no other label moves
    const span = placeAround(t, pin !== null && v?.overrideFree ? p.visitMinutes : len, p, lunch);
    if (pin !== null) out.push({ position: pos, startMinutes: pin, endMinutes: pin + len, minutes: len, booked: true, expectedStart: null, expectedEnd: null, passed: false, chosen: true });
    else out.push({ position: pos, startMinutes: span.start, endMinutes: span.end, minutes: len, booked: !!v, expectedStart: null, expectedEnd: null, passed: false });
    t = span.end;
  }
  if (!o.live) return out;
  const gap = o.gapMinutes ?? DEFAULT_GAP_MINUTES;
  const now = o.nowMinutes ?? null;
  // the running day keeps its own lunch: it may move once, after the visit the crew is on when lunch comes
  const liveLunch = lunchOf(p);
  const pinned = (v: PlannedVisit | undefined) => v?.startOverride != null;
  // 1.7.8: the day's order is by start time once a start was chosen — a pinned visit is outside the position order
  const started = visits.filter((v) => (v.arrivedAt != null || v.finishedAt != null) && !pinned(v));
  const lastStarted = started.length ? Math.max(...started.map((v) => v.position)) : 0;
  // the crew is busy until every started visit has ended, wherever on the day it was pinned
  let pinCursor: number | null = null;
  for (const v of visits) {
    if (!pinned(v) || (v.arrivedAt == null && v.finishedAt == null)) continue;
    const begun = v.arrivedAt ?? v.startOverride!;
    const len = v.minutes > 0 ? v.minutes : p.visitMinutes;
    pinCursor = Math.max(pinCursor ?? 0, (v.finishedAt ?? begun + len) + gap);
  }
  const pinnedAhead = visits
    .filter((v) => pinned(v) && v.arrivedAt == null && v.finishedAt == null)
    .map((v) => ({ start: v.startOverride!, end: v.startOverride! + (v.minutes > 0 ? v.minutes : p.visitMinutes) }));
  let cursor: number | null = null;
  for (const x of out) {
    const v = byPos.get(x.position);
    if (v && (v.arrivedAt != null || v.finishedAt != null)) {
      x.expectedStart = v.arrivedAt ?? x.startMinutes;
      x.expectedEnd = v.finishedAt ?? x.expectedStart + x.minutes;
      if (!pinned(v)) cursor = x.expectedEnd + gap;
      continue;
    }
    if (pinned(v)) {
      // not started: it stays where the office put it ("planned 3:30")
      x.expectedStart = x.startMinutes;
      x.expectedEnd = x.endMinutes;
      continue;
    }
    if (x.position < lastStarted) {
      // the crew is past it: a free time is passed; a visit not started stays as it was (Pending close-out)
      if (!v) x.passed = true;
      continue;
    }
    const free = cursor === null ? pinCursor : pinCursor === null ? cursor : Math.max(cursor, pinCursor);
    if (free === null) {
      // the crew has not started: the plan stands, but a free time already gone is passed
      if (!v && now !== null && x.startMinutes < now) x.passed = true;
      continue;
    }
    let from = Math.max(free, now ?? free);
    let span = placeAround(from, v ? x.minutes : 0, p, liveLunch);
    // 15 Oct 2026: a visit at a time the office chose (not started) holds that time — the running day flows around it,
    // so the next visit is not expected inside it (217 was "expected 1:00" over 512's chosen 1:00–2:30)
    for (let guard = 0; v && guard < 12; guard++) {
      const hit = pinnedAhead.find((w) => span.start < w.end && span.end > w.start);
      if (!hit) break;
      from = hit.end + gap;
      span = placeAround(from, x.minutes, p, liveLunch);
    }
    x.expectedStart = span.start;
    x.expectedEnd = v ? span.end : null;
    if (v) cursor = span.end + gap;
  }
  return out;
}

export interface Offer {
  position: number;
  minutes: number;
  /** When the new visit would start (today, live: the expected time). */
  startMinutes: number;
  endMinutes: number;
  /** The position's planned label. */
  scheduledStart: number;
  /** Ends by the crew's end of day (DAY_PLAN_END) and is not a passed time. */
  fits: boolean;
  passed: boolean;
  /** The latest a visit this long can start and still end by the end of day. */
  latestStart: number;
  endOfDay: number;
  /** With this visit in, the day's last visit would end after the end of day (null: the day still fits). */
  dayOverrunsTo: number | null;
}

/** Whether a visit of this length can go at this position (Book, Reschedule, Put on hold — the end-of-day rule). */
export function offerFor(dayKey: string, settings: DayPlanSettings, visits: readonly PlannedVisit[], count: number, want: { position: number; minutes: number }, o: LiveOptions = {}): Offer {
  const p = dayPlanFor(dayKey, settings);
  const others = visits.filter((v) => v.position !== want.position);
  const before = planDay(dayKey, settings, others, count, o).find((x) => x.position === want.position);
  const withIt = planDay(dayKey, settings, [...others, { position: want.position, minutes: want.minutes }], count, o);
  const me = withIt.find((x) => x.position === want.position)!;
  const start = o.live && me.expectedStart !== null ? me.expectedStart : me.startMinutes;
  const end = o.live && me.expectedEnd !== null ? me.expectedEnd : me.endMinutes;
  const passed = !!before?.passed;
  const last = Math.max(...withIt.filter((x) => x.booked).map((x) => (o.live && x.expectedEnd !== null ? x.expectedEnd : x.endMinutes)));
  return {
    position: want.position,
    minutes: want.minutes,
    startMinutes: start,
    endMinutes: end,
    scheduledStart: me.startMinutes,
    fits: end <= p.endMinutes && !passed,
    passed,
    latestStart: p.endMinutes - want.minutes,
    endOfDay: p.endMinutes,
    dayOverrunsTo: last > p.endMinutes ? last : null,
  };
}

/**
 * What a free position says on Book for a visit this long (1.5.2): `free` when it ends by the crew's end of day
 * (DAY_PLAN_END), `afterHours` when it would end after it — never shown as free; choosing it saves the booking as
 * Awaiting the Director's OK — and `passed` for a time the crew has gone past today.
 */
export function slotHours(dayKey: string, settings: DayPlanSettings, visits: readonly PlannedVisit[], count: number, position: number, minutes: number, o: LiveOptions = {}): 'free' | 'afterHours' | 'passed' {
  const offer = offerFor(dayKey, settings, visits, Math.max(count, position), { position, minutes }, o);
  return offer.passed ? 'passed' : offer.fits ? 'free' : 'afterHours';
}

/**
 * Minutes per unit (1.5.0): Arrived → Finish. `finishedClock` (hh:mm, the close-out's "time finished", which the
 * person may edit) wins over the Finish press when it is given. Null when it cannot be read or is not positive.
 */
export function minutesBetweenStamps(arrivedIso: string, finishedIso: string, finishedClock: string | null): number | null {
  const a = Date.parse(arrivedIso);
  let f = Date.parse(finishedIso);
  if (!Number.isFinite(a) || !Number.isFinite(f)) return null;
  if (finishedClock && /^\d{2}:\d{2}$/.test(finishedClock)) {
    // the edited clock time on the Finish press's day (the service clock): move the press to that minute
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' }).formatToParts(new Date(f));
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
    const pressed = get('hour') * 60 + get('minute');
    const [hh, mm] = finishedClock.split(':').map(Number);
    f += ((hh ?? 0) * 60 + (mm ?? 0) - pressed) * 60_000;
  }
  const m = Math.round((f - a) / 60_000);
  return m > 0 && m < 12 * 60 ? m : null;
}

/** "5:00 PM" / "5:00 p. m." — the end of day said in words. */
export function clockWithPeriod(minutes: number, lang: 'en' | 'es'): string {
  const pm = Math.floor(minutes / 60) >= 12;
  return `${formatClock(minutes)} ${lang === 'es' ? (pm ? 'p. m.' : 'a. m.') : pm ? 'PM' : 'AM'}`;
}

/** Why a visit is not offered as a normal slot, in words (the Book sheet; the API's refusal). */
export function afterHoursWords(o: Pick<Offer, 'endOfDay' | 'latestStart' | 'minutes'>, lang: 'en' | 'es'): string {
  const len = lengthWords(o.minutes);
  return lang === 'es'
    ? `El equipo termina a las ${clockWithPeriod(o.endOfDay, 'es')}; la última visita de ${len} puede empezar a las ${formatClock(o.latestStart)}. Más tarde necesita la aprobación del Director.`
    : `The crew finishes at ${clockWithPeriod(o.endOfDay, 'en')}; the last ${len} visit can start at ${formatClock(o.latestStart)} — later needs the Director’s approval.`;
}

/* ------------------------------------------------------------------------------------------------
 * 1.5.2 — arrival windows: "positions, not clock times" (Elijah, 2 Oct 2026).
 *
 * Each position of the day has a planned window — what tenants are told: "between 10:30 and 12:00". The windows
 * are a setting (DAY_PLAN_WINDOWS, "What we tell tenants" on the Day plan card), Elijah's by default; a position
 * past the listed ones (and every position on a day with its own early start) gets the rule: its planned start to
 * the next position's planned start (or lunch, or the end of the day), on the half hour. Tenant emails and the
 * tenant page say the window; Today, Book and the crew keep the exact planned or expected time. Nothing here
 * changes by itself: the observed times (History) are shown beside the plan for Elijah to adjust by eye.
 * ---------------------------------------------------------------------------------------------- */

export const DAY_PLAN_WINDOWS_KEY = 'DAY_PLAN_WINDOWS';
/** Elijah's windows (2 Oct 2026): 1st 9:30–10:30, 2nd 10:30–12:00, 3rd 1:00–2:00, 4th 2:00–3:30. */
export const DEFAULT_VISIT_WINDOWS = '09:30-10:30, 10:30-12:00, 13:00-14:00, 14:00-15:30';
/** A Tech Support visit's window: its time to this many minutes later. */
export const TECH_WINDOW_MINUTES = 30;

export interface VisitWindow {
  position: number;
  from: number;
  to: number;
}

const floor30 = (m: number) => Math.floor(m / 30) * 30;
const ceil30 = (m: number) => Math.ceil(m / 30) * 30;

/** "09:30-10:30, 10:30-12:00" → the windows in order; null when the text is not a list of windows. */
export function parseWindows(v: string | number | null | undefined): { from: number; to: number }[] | null {
  if (typeof v !== 'string' || !v.trim()) return null;
  const out: { from: number; to: number }[] = [];
  for (const part of v.split(/[,;]/).map((x) => x.trim()).filter(Boolean)) {
    const m = /^(\d{1,2}:\d{2})\s*(?:-|–|to)\s*(\d{1,2}:\d{2})$/i.exec(part);
    const from = m ? parseClock(m[1]!) : null;
    const to = m ? parseClock(m[2]!) : null;
    if (from === null || to === null || to <= from) return null;
    out.push({ from, to });
  }
  return out.length ? out : null;
}

/** The windows written back as the setting's text (24-hour, so it is never ambiguous). */
export function formatWindows(ws: readonly { from: number; to: number }[]): string {
  return ws.map((x) => `${formatClock(x.from, '24h')}-${formatClock(x.to, '24h')}`).join(', ');
}

/** The rule for a position the setting does not list: its planned start to the next start (or lunch, or the end). */
function derivedWindow(dayKey: string, settings: DayPlanSettings, position: number): VisitWindow | null {
  const p = dayPlanFor(dayKey, settings);
  const times = positionTimes(dayKey, settings, position + 1);
  const me = times.find((x) => x.position === position);
  if (!me) return null;
  const next = times.find((x) => x.position === position + 1);
  let to = next ? next.startMinutes : Math.max(p.endMinutes, me.endMinutes);
  if (p.lunchMinutes > 0 && me.startMinutes < p.lunchStartMinutes && to > p.lunchStartMinutes) to = Math.max(p.lunchStartMinutes, me.endMinutes);
  const from = floor30(me.startMinutes);
  return { position, from, to: Math.max(from + 30, ceil30(to)) };
}

/** The windows of a day, position 1…count (at least the listed ones). */
export function visitWindows(dayKey: string, settings: DayPlanSettings = {}, count = 0): VisitWindow[] {
  const ownStart = parseClock(settings[`${DAY_PLAN_OVERRIDE_PREFIX}${dayKey}`]) !== null;
  const listed = ownStart ? [] : (parseWindows(settings[DAY_PLAN_WINDOWS_KEY]) ?? parseWindows(DEFAULT_VISIT_WINDOWS)!);
  const out: VisitWindow[] = [];
  for (let pos = 1; pos <= Math.max(count, listed.length); pos++) {
    const l = listed[pos - 1];
    const w = l ? { position: pos, ...l } : derivedWindow(dayKey, settings, pos);
    if (w) out.push(w);
  }
  return out;
}

/** One position's window, or null when the plan has no room for it. */
export function windowFor(dayKey: string, position: number, settings: DayPlanSettings = {}): VisitWindow | null {
  return visitWindows(dayKey, settings, position).find((x) => x.position === position) ?? null;
}

/** A Tech Support visit's window: its time to half an hour later. */
export function techWindow(startMinutes: number): { from: number; to: number } {
  return { from: startMinutes, to: startMinutes + TECH_WINDOW_MINUTES };
}

/** "between 10:30 and 12:00" / "entre las 10:30 y las 12:00" (la 1:00) — what a tenant is told. */
export function windowWords(w: { from: number; to: number }, lang: 'en' | 'es'): string {
  if (lang === 'es') {
    const art = (m: number) => (Math.floor(m / 60) % 12 === 1 ? 'la' : 'las');
    return `entre ${art(w.from)} ${formatClock(w.from)} y ${art(w.to)} ${formatClock(w.to)}`;
  }
  return `between ${formatClock(w.from)} and ${formatClock(w.to)}`;
}
